import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Search, CornerDownLeft, ArrowUp, ArrowDown, User as UserIcon, Briefcase, ShieldCheck, X, Clock, Sparkles, ChevronRight } from "lucide-react";
import { useAuthStore } from "@/app/store/auth.store";
import { cn } from "@/lib/utils/cn";
import { useNavSections } from "./Sidebar";

// Extra words a page should also match on, plus a one-line description,
// keyed by route -- what people actually type ("salary", "wfh", "punch in")
// isn't always the menu label.
const META = {
  "/": { desc: "Your dashboard at a glance", keywords: "home dashboard overview" },
  "/onboarding": { desc: "New joiners and their checklists", keywords: "joining new hire" },
  "/employees": { desc: "Everyone's employee records", keywords: "staff people team members" },
  "/part-time": { desc: "Part-time staff records", keywords: "part time" },
  "/freelancers": { desc: "Freelancer profiles and contracts", keywords: "contractor" },
  "/directory": { desc: "Find colleagues and contact details", keywords: "contacts phone email people" },
  "/org-chart": { desc: "Who reports to whom", keywords: "hierarchy reporting manager tree" },
  "/attendance": { desc: "Check-ins, corrections and the monthly matrix", keywords: "check in check out punch present absent late half day correction regularization matrix" },
  "/team-activity": { desc: "Desktop tracker: active and idle time", keywords: "tracker desktop active idle monitoring productivity" },
  "/leave": { desc: "Apply for and approve leave and WFH", keywords: "time off vacation sick casual apply wfh work from home" },
  "/holidays": { desc: "This year's holiday calendar", keywords: "calendar festival off" },
  "/payroll": { desc: "Salary runs, payslips and deductions", keywords: "salary payslip pay ctc deduction late" },
  "/expenses": { desc: "Submit and track your claims", keywords: "reimbursement claim bill" },
  "/expenses-approvals": { desc: "Claims waiting for your approval", keywords: "reimbursement approve" },
  "/recruitment": { desc: "Hiring pipeline and job postings", keywords: "hiring jobs interview" },
  "/recruitment/candidates": { desc: "Every candidate in the system", keywords: "applicants resume" },
  "/offers": { desc: "Offer letters sent and accepted", keywords: "offer letter" },
  "/resignation": { desc: "Your resignation and notice period", keywords: "resign exit notice" },
  "/resignations": { desc: "Resignations to review", keywords: "resign exit notice" },
  "/assets": { desc: "Laptops and equipment", keywords: "laptop device equipment" },
  "/performance": { desc: "Reviews and goals", keywords: "review appraisal goals kpi" },
  "/announcements": { desc: "Company news", keywords: "news notice" },
  "/company-records": { desc: "Policies and company documents", keywords: "policy handbook" },
  "/settings/organisation/shifts": { desc: "Shift timings and grace period", keywords: "shift timing grace late" },
  "/settings/organisation/leave-management": { desc: "Leave types, limits and WFH rules", keywords: "leave policy types balance wfh rules" },
  "/settings/offer-letter-signatures": { desc: "Signatures and company stamp on offers", keywords: "sign stamp seal" },
};
const POPULAR = ["/attendance", "/leave", "/payroll", "/holidays", "/employees", "/directory"];
const RECENT_KEY = "w4h.moduleSearch.recent";

function readRecent() {
  try { return JSON.parse(localStorage.getItem(RECENT_KEY)) || []; } catch { return []; }
}
function pushRecent(to) {
  try { localStorage.setItem(RECENT_KEY, JSON.stringify([to, ...readRecent().filter((t) => t !== to)].slice(0, 5))); } catch { /* storage unavailable */ }
}

// Match + score. Returns null for no match, or { score, ranges } where
// ranges are [start, end) spans of the label to highlight. Prefix beats
// word-start beats substring beats in-order letters ("atnd" ->
// Attendance) beats a keyword/description hit.
function match(item, q) {
  const label = item.label.toLowerCase();
  const idx = label.indexOf(q);
  if (idx === 0) return { score: 5, ranges: [[0, q.length]] };
  if (idx > 0 && label[idx - 1] === " ") return { score: 4, ranges: [[idx, idx + q.length]] };
  if (idx > 0) return { score: 3, ranges: [[idx, idx + q.length]] };
  const ranges = [];
  let j = 0;
  for (let i = 0; i < label.length && j < q.length; i += 1) {
    if (label[i] === q[j]) { ranges.push([i, i + 1]); j += 1; }
  }
  if (j === q.length && q.length >= 3) return { score: 2, ranges };
  if (`${item.keywords} ${item.desc} ${item.section}`.toLowerCase().includes(q)) return { score: 1, ranges: [] };
  return null;
}

function Highlight({ text, ranges }) {
  if (!ranges?.length) return text;
  const out = [];
  let last = 0;
  ranges.forEach(([a, b], i) => {
    if (a > last) out.push(text.slice(last, a));
    out.push(<mark key={i} className="rounded-[3px] bg-violet-200/70 px-[1px] text-inherit dark:bg-violet-500/40">{text.slice(a, b)}</mark>);
    last = b;
  });
  out.push(text.slice(last));
  return out;
}

// Header search: type a module name, pick it, and go straight to that
// page. Only ever offers pages this user can see in the sidebar (same
// useNavSections filter), plus their own account pages. Opens with a
// click on the bar or Ctrl/Cmd+K anywhere; arrow keys + Enter to pick.
export function ModuleSearch() {
  const navigate = useNavigate();
  const sections = useNavSections();
  const user = useAuthStore((s) => s.user);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);
  const [recent, setRecent] = useState([]);
  const inputRef = useRef(null);
  const listRef = useRef(null);

  const allItems = useMemo(() => {
    const seen = new Set();
    const items = [];
    const add = (i, section) => {
      const path = i.to.split("?")[0];
      if (seen.has(i.to)) return;
      seen.add(i.to);
      items.push({ to: i.to, label: i.label, icon: i.icon, section, desc: i.desc || META[path]?.desc || "", keywords: i.keywords || META[path]?.keywords || "" });
    };
    for (const s of sections) for (const i of s.items) add(i, s.section || "General");
    add({ to: "/settings/profile", label: "Your profile", icon: UserIcon, desc: "Photo, name and preferences", keywords: "photo picture avatar settings theme" }, "Account");
    if (user?.employeeId && !user?.isSystemAccount) {
      add({ to: `/employees/${user.employeeId}`, label: "My details", icon: Briefcase, desc: "Your full employee record", keywords: "my profile employment personal" }, "Account");
    }
    add({ to: "/settings/security", label: "Security", icon: ShieldCheck, desc: "Password and sessions", keywords: "password 2fa login" }, "Account");
    return items;
  }, [sections, user?.employeeId, user?.isSystemAccount]);

  const q = query.trim().toLowerCase();

  // Browsing (empty box): Recent, Popular, then everything by section.
  // Searching: one relevance-sorted list. Keyboard order always follows
  // the flattened `groups` order exactly.
  const groups = useMemo(() => {
    if (q) {
      const results = allItems
        .map((item, idx) => ({ item, idx, m: match(item, q) }))
        .filter((r) => r.m)
        .sort((a, b) => b.m.score - a.m.score || a.idx - b.idx)
        .map((r) => ({ ...r.item, ranges: r.m.ranges }));
      return results.length ? [{ title: `${results.length} result${results.length === 1 ? "" : "s"}`, items: results, layout: "list" }] : [];
    }
    const byTo = new Map(allItems.map((i) => [i.to, i]));
    const out = [];
    const recentItems = recent.map((t) => byTo.get(t)).filter(Boolean);
    if (recentItems.length) out.push({ title: "Recent", icon: Clock, items: recentItems, layout: "list" });
    const popular = POPULAR.map((t) => byTo.get(t)).filter((i) => i && !recent.includes(i.to));
    if (popular.length) out.push({ title: "Popular", icon: Sparkles, items: popular, layout: "grid" });
    const shown = new Set([...recentItems, ...popular].map((i) => i.to));
    const bySection = new Map();
    for (const i of allItems) {
      if (shown.has(i.to)) continue;
      if (!bySection.has(i.section)) bySection.set(i.section, []);
      bySection.get(i.section).push(i);
    }
    for (const [title, items] of bySection) out.push({ title, items, layout: "grid" });
    return out;
  }, [allItems, q, recent]);

  const flat = useMemo(() => groups.flatMap((g) => g.items), [groups]);

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen((o) => !o);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    setQuery("");
    setActiveIndex(0);
    setRecent(readRecent());
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    requestAnimationFrame(() => inputRef.current?.focus());
    return () => { document.body.style.overflow = prevOverflow; };
  }, [open]);

  useEffect(() => setActiveIndex(0), [query]);

  useEffect(() => {
    listRef.current?.querySelector(`[data-index="${activeIndex}"]`)?.scrollIntoView({ block: "nearest" });
  }, [activeIndex]);

  const go = (item) => {
    if (!item) return;
    pushRecent(item.to);
    setOpen(false);
    navigate(item.to);
  };

  const onInputKeyDown = (e) => {
    if (e.key === "ArrowDown" || (e.key === "Tab" && !e.shiftKey)) {
      e.preventDefault();
      setActiveIndex((i) => (flat.length ? (i + 1) % flat.length : 0));
    } else if (e.key === "ArrowUp" || (e.key === "Tab" && e.shiftKey)) {
      e.preventDefault();
      setActiveIndex((i) => (flat.length ? (i - 1 + flat.length) % flat.length : 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      go(flat[activeIndex]);
    } else if (e.key === "Escape") {
      e.preventDefault();
      if (query) setQuery("");
      else setOpen(false);
    }
  };

  const isMac = typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform);
  const kbd = "inline-flex h-5 min-w-5 items-center justify-center rounded-md border border-border bg-background px-1.5 font-mono text-[10.5px] font-semibold text-muted-foreground shadow-[0_1px_0_rgba(0,0,0,0.06)] dark:border-white/10 dark:bg-white/[0.06]";

  let running = -1;

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label="Search modules"
        className="group flex h-10 items-center gap-2.5 rounded-xl border border-border/80 bg-background/80 pl-3 pr-2 text-[14px] text-muted-foreground shadow-[0_1px_2px_rgba(20,10,60,0.04)] transition-all hover:border-violet-300 hover:bg-white hover:text-foreground hover:shadow-[0_4px_14px_-6px_rgba(92,63,206,0.35)] dark:border-white/10 dark:bg-white/[0.04] dark:hover:border-violet-400/40 dark:hover:bg-white/[0.07] sm:w-72 md:w-96"
      >
        <Search className="h-4 w-4 shrink-0 text-violet-500 transition-transform group-hover:scale-110" />
        <span className="hidden flex-1 truncate text-left sm:block">Search modules and pages</span>
        <span className="hidden items-center gap-1 sm:inline-flex">
          <kbd className={kbd}>{isMac ? "⌘" : "Ctrl"}</kbd>
          <kbd className={kbd}>K</kbd>
        </span>
      </button>

      {createPortal(
        <AnimatePresence>
          {open && (
            <div className="fixed inset-0 z-[100] flex items-start justify-center px-3 pt-[10vh] sm:px-4" role="dialog" aria-modal="true" aria-label="Search modules">
              <motion.div
                className="absolute inset-0 bg-slate-950/45 backdrop-blur-[6px]"
                onClick={() => setOpen(false)}
                aria-hidden="true"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.15 }}
              />
              <motion.div
                className="relative flex max-h-[78vh] w-full max-w-2xl flex-col overflow-hidden rounded-2xl border border-white/60 bg-surface shadow-[0_40px_100px_-30px_rgba(30,20,90,0.55)] ring-1 ring-black/5 dark:border-white/10 dark:bg-[#151a2c]"
                initial={{ opacity: 0, y: -12, scale: 0.98 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: -8, scale: 0.98 }}
                transition={{ duration: 0.16, ease: "easeOut" }}
              >
                {/* Search field */}
                <div className="flex items-center gap-3 border-b border-border px-5 dark:border-white/10">
                  <Search className="h-5 w-5 shrink-0 text-violet-500" />
                  <input
                    ref={inputRef}
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    onKeyDown={onInputKeyDown}
                    placeholder="Where do you want to go?"
                    className="h-16 w-full border-0 bg-transparent text-[17px] font-medium text-foreground shadow-none outline-none ring-0 placeholder:font-normal placeholder:text-muted-foreground/80 focus:outline-none focus:ring-0 focus-visible:outline-none focus-visible:ring-0 focus-visible:ring-offset-0"
                    role="combobox"
                    aria-expanded="true"
                    aria-controls="module-search-results"
                    aria-activedescendant={flat[activeIndex] ? `module-search-${activeIndex}` : undefined}
                    autoComplete="off"
                    spellCheck={false}
                  />
                  {query ? (
                    <button type="button" onClick={() => { setQuery(""); inputRef.current?.focus(); }} className="grid h-7 w-7 shrink-0 place-items-center rounded-full text-muted-foreground hover:bg-muted hover:text-foreground" aria-label="Clear search">
                      <X className="h-4 w-4" />
                    </button>
                  ) : (
                    <button type="button" onClick={() => setOpen(false)} className={cn(kbd, "shrink-0 hover:text-foreground")}>Esc</button>
                  )}
                </div>

                {/* Results */}
                <div ref={listRef} id="module-search-results" role="listbox" className="flex-1 overflow-y-auto overscroll-contain px-3 pb-3 pt-2">
                  {!flat.length ? (
                    <div className="flex flex-col items-center px-6 py-14 text-center">
                      <span className="grid h-12 w-12 place-items-center rounded-2xl bg-violet-50 text-violet-500 dark:bg-violet-500/10">
                        <Search className="h-5 w-5" />
                      </span>
                      <p className="mt-4 text-[15px] font-semibold text-foreground">Nothing matches "{query}"</p>
                      <p className="mt-1 text-[13px] text-muted-foreground">Try another word -- like "leave", "salary" or "holiday".</p>
                    </div>
                  ) : (
                    groups.map((group) => {
                      const GroupIcon = group.icon;
                      return (
                        <section key={group.title} className="mt-2 first:mt-1">
                          <p className="flex items-center gap-1.5 px-2 pb-1.5 pt-2 text-[11px] font-bold uppercase tracking-[0.12em] text-muted-foreground">
                            {GroupIcon && <GroupIcon className="h-3.5 w-3.5" aria-hidden="true" />}
                            {group.title}
                          </p>
                          <div className={cn(group.layout === "grid" ? "grid grid-cols-1 gap-1 sm:grid-cols-2" : "space-y-0.5")}>
                            {group.items.map((item) => {
                              running += 1;
                              const index = running;
                              const Icon = item.icon;
                              const active = index === activeIndex;
                              return (
                                <button
                                  key={`${group.title}-${item.to}`}
                                  type="button"
                                  id={`module-search-${index}`}
                                  data-index={index}
                                  role="option"
                                  aria-selected={active}
                                  onMouseMove={() => active || setActiveIndex(index)}
                                  onClick={() => go(item)}
                                  className={cn(
                                    "group/item flex w-full items-center gap-3 rounded-xl px-2.5 py-2 text-left outline-none transition-colors focus-visible:ring-0 focus-visible:ring-offset-0",
                                    active ? "bg-violet-50 dark:bg-violet-500/15" : "hover:bg-muted/60"
                                  )}
                                >
                                  <span className={cn(
                                    "grid h-9 w-9 shrink-0 place-items-center rounded-[10px] transition-colors",
                                    active ? "bg-gradient-to-br from-violet-500 to-indigo-500 text-white shadow-[0_6px_14px_-6px_rgba(92,63,206,0.8)]" : "bg-violet-50 text-violet-600 dark:bg-white/[0.06] dark:text-violet-300"
                                  )}>
                                    {Icon && <Icon className="h-[17px] w-[17px]" aria-hidden="true" />}
                                  </span>
                                  <span className="min-w-0 flex-1">
                                    <span className={cn("block truncate text-[14.5px] font-semibold", active ? "text-violet-900 dark:text-violet-100" : "text-foreground")}>
                                      <Highlight text={item.label} ranges={item.ranges} />
                                    </span>
                                    {item.desc && <span className="block truncate text-[12.5px] text-muted-foreground">{item.desc}</span>}
                                  </span>
                                  {q && (
                                    <span className="hidden shrink-0 rounded-full bg-muted px-2 py-0.5 text-[11px] font-medium text-muted-foreground sm:inline-block dark:bg-white/[0.06]">
                                      {item.section}
                                    </span>
                                  )}
                                  {active
                                    ? <CornerDownLeft className="h-4 w-4 shrink-0 text-violet-500" aria-hidden="true" />
                                    : <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground/0 transition-colors group-hover/item:text-muted-foreground/60" aria-hidden="true" />}
                                </button>
                              );
                            })}
                          </div>
                        </section>
                      );
                    })
                  )}
                </div>

                {/* Footer */}
                <div className="flex items-center gap-4 border-t border-border bg-muted/30 px-5 py-2.5 text-[12px] text-muted-foreground dark:border-white/10 dark:bg-white/[0.02]">
                  <span className="inline-flex items-center gap-1.5"><kbd className={kbd}><ArrowUp className="h-3 w-3" /></kbd><kbd className={kbd}><ArrowDown className="h-3 w-3" /></kbd>move</span>
                  <span className="inline-flex items-center gap-1.5"><kbd className={kbd}><CornerDownLeft className="h-3 w-3" /></kbd>open</span>
                  <span className="inline-flex items-center gap-1.5"><kbd className={kbd}>Esc</kbd>{query ? "clear" : "close"}</span>
                  <span className="ml-auto hidden sm:inline">{allItems.length} pages</span>
                </div>
              </motion.div>
            </div>
          )}
        </AnimatePresence>,
        document.body
      )}
    </>
  );
}
